import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import adminService from '../services/adminService';

const OFFICE_ADDRESS = 'Old No: 39, New No: 2,\nFirst Floor, Ramasamy Street,\nT.Nagar, Chennai-600017';
const APPROVAL_TEXT = 'Approved by the Ministry of External Affairs, India\nLicense No:B-CHENNAI/PROPRIETORSHIP/5533681/2019';

const defaultData = {
  name: '',
  title: 'Human Resource Representative',
  employeeId: '',
  dob: '',
  blood: '',
  contact: '',
  address: '',
  dateJoined: '',
  officeAddress: OFFICE_ADDRESS,
  emergencyNumber: '',
  photoUrl: ''
};

/* ─── Card Front ─────────────────────────────────────────────── */
const CardFront = ({ data, customFields }) => {
  const fields = [
    { label: 'EMP ID',  value: data.employeeId },
    { label: 'DOB',     value: data.dob },
    { label: 'BLOOD',   value: data.blood },
    { label: 'CONTACT', value: data.contact },
    { label: 'ADDRESS', value: data.address },
    ...customFields.filter(f => f.label)
  ];

  return (
    <div style={{
      width: '300px',
      minHeight: '520px',
      borderRadius: '18px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
      fontFamily: "'Inter', Arial, sans-serif",
      background: '#ffffff',
      position: 'relative',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e5e7eb'
    }}>

      {/* ── Top-right header waves ── */}
      {/* Accent (lighter blue, behind) */}
      <div style={{
        position: 'absolute', top: 0, right: 0,
        width: '100%', height: '220px',
        background: '#0047AB',
        clipPath: 'ellipse(70% 50% at 90% 5%)',
        opacity: 0.5, zIndex: 0
      }} />
      {/* Main dark navy wave */}
      <div style={{
        position: 'absolute', top: 0, right: 0,
        width: '100%', height: '185px',
        background: '#002D62',
        clipPath: 'ellipse(80% 60% at 85% 10%)',
        zIndex: 1
      }} />

      {/* ── Bottom-left footer waves ── */}
      {/* Accent (lighter, behind) */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: '165px', height: '165px',
        background: '#0047AB',
        clipPath: 'ellipse(80% 80% at 10% 100%)',
        opacity: 0.4, zIndex: 0
      }} />
      {/* Main dark navy wave */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: '140px', height: '140px',
        background: '#002D62',
        clipPath: 'ellipse(80% 80% at 20% 100%)',
        zIndex: 1
      }} />

      {/* ── Company name — top right ── */}
      <div style={{
        position: 'absolute', top: '20px', right: '18px',
        textAlign: 'right', zIndex: 10
      }}>
        <p style={{
          color: '#ffffff', fontWeight: '800', fontSize: '15px',
          letterSpacing: '0.5px', lineHeight: '1.35', margin: 0
        }}>
          JUPITER<br />INTERNATIONAL
        </p>
      </div>

      {/* ── Profile photo ── */}
      <div style={{
        position: 'relative', zIndex: 10,
        display: 'flex', justifyContent: 'center',
        marginTop: '72px'
      }}>
        <div style={{
          width: '120px', height: '120px', borderRadius: '50%',
          border: '4px solid #002D62',
          overflow: 'hidden', background: '#e2e8f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)'
        }}>
          {data.photoUrl ? (
            <img src={data.photoUrl} alt="Profile"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 24 24" fill="#94a3b8" width="54" height="54">
                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* ── Name & title ── */}
      <div style={{
        position: 'relative', zIndex: 10,
        textAlign: 'center', padding: '18px 20px 0'
      }}>
        <p style={{
          fontWeight: '800', fontSize: '17px', color: '#111827',
          letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 5px'
        }}>
          {data.name || 'FULL NAME'}
        </p>
        <p style={{ fontSize: '11px', color: '#6b7280', fontWeight: '400', margin: 0 }}>
          {data.title || 'Job Title'}
        </p>
      </div>

      {/* ── Detail fields ── */}
      <div style={{
        position: 'relative', zIndex: 10,
        padding: '18px 28px 32px', flex: 1
      }}>
        {fields.map((f, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start',
            marginBottom: '10px', fontSize: '11px'
          }}>
            <span style={{
              fontWeight: '700', minWidth: '72px', color: '#1f2937',
              textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: '1.5'
            }}>{f.label}</span>
            <span style={{ margin: '0 8px', color: '#4b5563', lineHeight: '1.5' }}>:</span>
            <span style={{
              color: '#374151', whiteSpace: 'pre-line', flex: 1,
              lineHeight: '1.45', fontWeight: '600'
            }}>{f.value || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Card Back ──────────────────────────────────────────────── */
const CardBack = ({ data }) => {
  const fields = [
    { label: 'Date Joined', value: data.dateJoined },
    { label: 'Office\nAddress', value: data.officeAddress },
    { label: 'Emergency\nNumber', value: data.emergencyNumber }
  ];

  return (
    <div className="id-card-back" style={{
      width: '320px', height: '500px', borderRadius: '16px', overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif',
      background: '#fff', position: 'relative', display: 'flex', flexDirection: 'column', flexShrink: 0
    }}>
      {/* Watermark logo */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 0, pointerEvents: 'none'
      }}>
        <img src="/logo_final.png" alt=""
          style={{ width: '180px', height: '180px', objectFit: 'contain', opacity: 0.07 }} />
      </div>

      <div style={{ padding: '22px 24px', flex: 1, position: 'relative', zIndex: 1 }}>
        <p style={{ fontSize: '10px', color: '#333', lineHeight: '1.6', marginBottom: '22px', whiteSpace: 'pre-line' }}>
          {APPROVAL_TEXT}
        </p>
        {fields.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '18px', fontSize: '11px' }}>
            <span style={{ fontWeight: 'bold', minWidth: '82px', color: '#222', whiteSpace: 'pre-line', lineHeight: '1.4' }}>{f.label}</span>
            <span style={{ margin: '0 8px', color: '#222' }}>:</span>
            <span style={{ color: '#444', whiteSpace: 'pre-line', flex: 1, lineHeight: '1.4' }}>{f.value || '—'}</span>
          </div>
        ))}
      </div>

      {/* Wave + logo */}
      <div style={{ position: 'relative' }}>
        <svg viewBox="0 0 320 70" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
          <path d="M0,35 C80,5 240,65 320,35 L320,70 L0,70 Z" fill="#1b3a5c" />
        </svg>
        <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)' }}>
          <img src="/logo_final.png" alt="Logo"
            style={{ width: '42px', height: '42px', objectFit: 'contain', filter: 'brightness(10)' }} />
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ──────────────────────────────────────────────── */
const AdminIDCardPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState(defaultData);
  const [customFields, setCustomFields] = useState([]);
  const [previewSide, setPreviewSide] = useState('front');
  const printAreaRef = useRef();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [hrRes, hroRes] = await Promise.all([
        adminService.getAllUsers({ limit: 100, role: 'HR' }),
        adminService.getAllUsers({ limit: 100, role: 'HRO' })
      ]);
      const combined = [
        ...(hrRes.success ? hrRes.data.users : []),
        ...(hroRes.success ? hroRes.data.users : [])
      ];
      setUsers(combined);
    } catch {
      toast.error('Failed to load users');
    }
  };

  const handleUserSelect = async (userId) => {
    setSelectedUserId(userId);
    if (!userId) { setCardData(defaultData); return; }
    try {
      setLoading(true);
      const result = await adminService.getUserDetails(userId);
      if (result.success) {
        const u = result.data;
        const titleMap = { HR: 'Human Resource Representative', HRO: 'HR Officer', ADMIN: 'Administrator', CHAIRMAN: 'Chairman' };
        // DOB, blood group, and emergency number are not stored in the
        // HR User model — admin must fill them in manually on this form.
        const dobRaw = u.dateOfBirth || u.dob || '';
        setCardData({
          name: u.name || '',
          title: titleMap[u.role] || u.role || '',
          employeeId: u.employeeId || '',
          dob: dobRaw ? new Date(dobRaw).toLocaleDateString('en-GB') : '',
          blood: u.bloodGroup || u.blood || '',
          contact: u.phoneNumber || '',
          address: u.place || '',
          dateJoined: u.joiningDate ? new Date(u.joiningDate).toLocaleDateString('en-GB') : '',
          officeAddress: OFFICE_ADDRESS,
          emergencyNumber: u.emergencyNumber || u.emergencyContact || '',
          photoUrl: u.profilePhotoUrl || u.profilePhoto || u.photoUrl || ''
        });
        setCustomFields([]);
      } else {
        toast.error('Failed to load user');
      }
    } catch {
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleField = (key, val) => setCardData(p => ({ ...p, [key]: val }));

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCardData(p => ({ ...p, photoUrl: URL.createObjectURL(file) }));
  };

  const addCustomField = () => setCustomFields(p => [...p, { label: '', value: '' }]);
  const updateCustom = (i, k, v) => setCustomFields(p => p.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const removeCustom = (i) => setCustomFields(p => p.filter((_, idx) => idx !== i));

  const handlePrint = () => {
    const printContent = printAreaRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>ID Card</title>
      <style>
        * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        body { margin: 0; padding: 20px; background: #f0f0f0; display: flex; gap: 30px; justify-content: center; align-items: flex-start; font-family: Arial, sans-serif; }
        @media print {
          body { background: #fff; padding: 10px; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      </style>
      </head><body>${printContent}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide";

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">ID Card Generator</h1>
          <p className="text-gray-500 text-sm mt-1">Select an HR user and customise their ID card, then print.</p>
        </div>

        <div className="flex flex-col xl:flex-row gap-8">
          {/* ── Left: Form ─────────────────────────────── */}
          <div className="flex-1 space-y-6">
            {/* User select */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-700 mb-4">Select Employee</h2>
              <select
                value={selectedUserId}
                onChange={e => handleUserSelect(e.target.value)}
                className={inputCls}
              >
                <option value="">— Choose HR / HRO user —</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.name} ({u.role}) {u.employeeId ? `· ${u.employeeId}` : ''}
                  </option>
                ))}
              </select>
              {loading && <p className="text-xs text-gray-400 mt-2">Loading user data…</p>}
            </div>

            {/* Photo upload */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-700 mb-4">Profile Photo</h2>
              <div className="flex items-center gap-4">
                <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0', border: '2px solid #cbd5e1', flexShrink: 0 }}>
                  {cardData.photoUrl ? (
                    <img src={cardData.photoUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg viewBox="0 0 24 24" fill="#94a3b8" width="36" height="36">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    </div>
                  )}
                </div>
                <label className="cursor-pointer bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium px-4 py-2 rounded-lg border border-blue-200 transition-colors">
                  Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                </label>
                {cardData.photoUrl && (
                  <button onClick={() => setCardData(p => ({ ...p, photoUrl: '' }))}
                    className="text-sm text-red-500 hover:text-red-700">Remove</button>
                )}
              </div>
            </div>

            {/* Front card fields */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-700 mb-4">Card Front Fields</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input className={inputCls} value={cardData.name} onChange={e => handleField('name', e.target.value)} placeholder="VINISHIYA A" />
                </div>
                <div>
                  <label className={labelCls}>Job Title</label>
                  <input className={inputCls} value={cardData.title} onChange={e => handleField('title', e.target.value)} placeholder="Human Resource Representative" />
                </div>
                <div>
                  <label className={labelCls}>Employee ID</label>
                  <input className={inputCls} value={cardData.employeeId} onChange={e => handleField('employeeId', e.target.value)} placeholder="EMP-001" />
                </div>
                <div>
                  <label className={labelCls}>Date of Birth</label>
                  <input className={inputCls} value={cardData.dob} onChange={e => handleField('dob', e.target.value)} placeholder="03/03/2002" />
                </div>
                <div>
                  <label className={labelCls}>Blood Group</label>
                  <input className={inputCls} value={cardData.blood} onChange={e => handleField('blood', e.target.value)} placeholder="O+ve" />
                </div>
                <div>
                  <label className={labelCls}>Contact Number</label>
                  <input className={inputCls} value={cardData.contact} onChange={e => handleField('contact', e.target.value)} placeholder="9876543210" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Address</label>
                  <textarea className={inputCls} rows={2} value={cardData.address} onChange={e => handleField('address', e.target.value)}
                    placeholder="No.4/4, Street, City - 600032" />
                </div>
              </div>

              {/* Custom fields */}
              {customFields.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom Fields</p>
                  {customFields.map((f, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input className={inputCls} placeholder="Label" value={f.label}
                        onChange={e => updateCustom(i, 'label', e.target.value)} style={{ maxWidth: 120 }} />
                      <input className={inputCls} placeholder="Value" value={f.value}
                        onChange={e => updateCustom(i, 'value', e.target.value)} />
                      <button onClick={() => removeCustom(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={addCustomField}
                className="mt-4 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                Add custom field
              </button>
            </div>

            {/* Back card fields */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-700 mb-4">Card Back Fields</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Date Joined</label>
                  <input className={inputCls} value={cardData.dateJoined} onChange={e => handleField('dateJoined', e.target.value)} placeholder="07/09/2023" />
                </div>
                <div>
                  <label className={labelCls}>Office Address</label>
                  <textarea className={inputCls} rows={3} value={cardData.officeAddress} onChange={e => handleField('officeAddress', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Emergency Number</label>
                  <input className={inputCls} value={cardData.emergencyNumber} onChange={e => handleField('emergencyNumber', e.target.value)} placeholder="8056 234 084" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Preview ──────────────────────────── */}
          <div className="xl:w-[380px]">
            <div className="sticky top-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-base font-semibold text-gray-700">Preview</h2>
                  <div className="flex rounded-lg overflow-hidden border border-gray-200">
                    <button onClick={() => setPreviewSide('front')}
                      className={`px-3 py-1 text-sm font-medium transition-colors ${previewSide === 'front' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      Front
                    </button>
                    <button onClick={() => setPreviewSide('back')}
                      className={`px-3 py-1 text-sm font-medium transition-colors ${previewSide === 'back' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      Back
                    </button>
                  </div>
                </div>

                <div className="flex justify-center">
                  {previewSide === 'front'
                    ? <CardFront data={cardData} customFields={customFields} />
                    : <CardBack data={cardData} />}
                </div>

                <button onClick={handlePrint}
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                    <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9H5v-2h1v2zm8 0h1v-2h-1v2zM7 13h6v3H7v-3z" clipRule="evenodd" />
                  </svg>
                  Print Both Sides
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print area with both sides side-by-side */}
      <div ref={printAreaRef} style={{ display: 'none' }}>
        <CardFront data={cardData} customFields={customFields} />
        <div style={{ width: '30px' }} />
        <CardBack data={cardData} />
      </div>
    </MainLayout>
  );
};

export default AdminIDCardPage;
